import React, { useEffect, useMemo, useState } from "react";
import type { Player } from "../../shared/models/Player";
import type { PlayerProfileEntry } from "../../shared/data/playerProfiles";
import {
  playerGameLogsById,
  type PlayerGameLogPosition,
  type PlayerGameLogScoring,
  type PlayerSeasonGameLogs,
} from "../../shared/data/playerGameLogs";
import { formatTeamAbbreviation, formatTeamFullName, getTeamPrimaryColor } from "../../shared/utils/teamAbbreviation";
import { posColor } from "../../shared/utils/posColor";
import TeamLogo from "../../shared/components/TeamLogo";
import PlayerGameLogTable from "./PlayerGameLogTable";
import PlayerCareerTable from "./PlayerCareerTable";
import { PLAYER_STATS_GRID_DEFAULT_ZOOM_INDEX } from "./playerStatsGridLayout";

type Props = {
  open?: boolean; // if omitted, default to true (back-compat)
  onClose: () => void;
  player: Player | null;
  links: PlayerProfileEntry[];
  gameLogs?: PlayerSeasonGameLogs[];
  rankingIds?: string[];
  rankingsRankingIds?: string[];
  expertRankingIds?: string[];
  playersById?: Record<string, Player>;
  onToggleDrafted?: (id: string) => void;
  draftedIds?: Set<string>;
  presentation?: "modal" | "embedded";
  rankLabel?: string;
};

function normalizePodcastKey(podcast: string): string {
  return podcast.trim();
}

function toSlugVariants(name: string): string[] {
  const trimmed = name.trim();
  const noQuotes = trimmed.replace(/^["']+|["']+$/g, "");
  const collapsed = noQuotes.replace(/\s+/g, " ");
  const noSpace = collapsed.replace(/\s+/g, "");
  const underscores = collapsed.replace(/\s+/g, "_");
  const dashes = collapsed.replace(/\s+/g, "-");
  const alnumOnly = collapsed.replace(/[^a-zA-Z0-9]/g, "");
  return Array.from(new Set([collapsed, noSpace, underscores, dashes, alnumOnly].filter(Boolean)));
}

function buildLogoCandidates(podcastName: string): string[] {
  const folders = ["/Podcasts", "/podcasts"];
  const exts = [".png", ".jpg", ".jpeg", ".webp", ".svg"];
  const bases = toSlugVariants(podcastName);

  const candidates: string[] = [];
  for (const folder of folders) {
    for (const base of bases) {
      for (const ext of exts) {
        candidates.push(`${folder}/${base}${ext}`);
      }
    }
  }
  return candidates;
}

function PodcastLogo({ podcast }: { podcast: string }) {
  const candidates = useMemo(() => buildLogoCandidates(podcast), [podcast]);
  const [idx, setIdx] = useState(0);

  const src = candidates[idx];

  if (!src) return null;

  return (
    <img
      src={src}
      alt={`${podcast} logo`}
      style={{
        width: 44,
        height: 44,
        borderRadius: 10,
        objectFit: "cover",
        flex: "0 0 auto",
        background: "rgba(255,255,255,0.08)",
      }}
      onError={() => setIdx((v) => v + 1)}
    />
  );
}

function getTeamWatermarkStyle(team: unknown): React.CSSProperties {
  const abbreviation = formatTeamAbbreviation(team);

  if (abbreviation === "NYJ") {
    return {
      opacity: 0.16,
      filter: "brightness(0) saturate(0) invert(1) drop-shadow(0 0 26px rgba(255,255,255,0.22))",
    };
  }

  if (abbreviation === "NYG") {
    return {
      opacity: 0.18,
      filter: "brightness(0) saturate(0) invert(1) drop-shadow(0 0 22px rgba(255,255,255,0.18))",
    };
  }

  if (abbreviation === "PHI" || abbreviation === "JAX" || abbreviation === "GB") {
    return {
      opacity: 0.18,
      filter: "brightness(1.2) saturate(0.85) drop-shadow(0 0 18px rgba(255,255,255,0.12))",
    };
  }

  return {
    opacity: 0.14,
  };
}

function getTeamProfileBackground(team: unknown, fallbackColor: string): string {
  const abbreviation = formatTeamAbbreviation(team);

  if (abbreviation === "NYJ") {
    return "linear-gradient(135deg, #2aa66f 0%, #1d7f56 42%, #0f4f38 100%)";
  }

  if (abbreviation === "NYG") {
    return "linear-gradient(135deg, #2358ba 0%, #1b418f 42%, #122a63 100%)";
  }

  return `linear-gradient(135deg, ${fallbackColor} 0%, color-mix(in srgb, ${fallbackColor} 82%, #0f172a 18%) 100%)`;
}

function getFantasyPointsForScoring(
  row: PlayerSeasonGameLogs["rows"][number],
  scoring: PlayerGameLogScoring
): number | undefined {
  if (scoring === "standard") return row.fantasyPointsStandard ?? row.fantasyPoints;
  if (scoring === "halfPpr") return row.fantasyPointsHalfPpr ?? row.fantasyPoints;
  return row.fantasyPointsPpr ?? row.fantasyPoints;
}

function formatPpg(value: number | null): string {
  if (value == null || !Number.isFinite(value)) return "—";
  return value.toFixed(1);
}

function formatSeasonShortLabel(season: number | null | undefined): string {
  if (season == null || !Number.isFinite(season)) return "—";
  return String(season).slice(-2).padStart(2, "0");
}

function getSeasonFantasyTotal(season: PlayerSeasonGameLogs | undefined, scoring: PlayerGameLogScoring): number | null {
  if (!season) return null;
  const points = season.rows
    .map((row) => getFantasyPointsForScoring(row, scoring))
    .filter((value): value is number => value != null && Number.isFinite(value));
  if (!points.length) return null;
  return points.reduce((sum, value) => sum + value, 0);
}

function isNativeIosShell() {
  if (typeof window === "undefined") return false;
  const capacitor = (window as any).Capacitor;
  if (capacitor?.isNativePlatform?.()) return true;
  return window.location.protocol === "capacitor:";
}

export default function PlayerProfileModal({
  open,
  onClose,
  player,
  links,
  gameLogs = [],
  rankingIds = [],
  playersById = {},
  onToggleDrafted,
  draftedIds,
  presentation = "modal",
}: Props) {
  const isEmbedded = presentation === "embedded";
  const isOpen = open ?? true;
  const rootRef = React.useRef<HTMLDivElement | null>(null);
  const lastDraftTogglePointerAtRef = React.useRef(0);
  const lastClosePointerAtRef = React.useRef(0);
  const closePointerTimeoutRef = React.useRef<number | null>(null);
  const [viewportWidth, setViewportWidth] = useState<number>(() =>
    typeof window === "undefined" ? 1280 : window.innerWidth
  );
  const [viewportHeight, setViewportHeight] = useState<number>(() =>
    typeof window === "undefined" ? 844 : window.innerHeight
  );
  const [embeddedWidth, setEmbeddedWidth] = useState<number>(0);
  const [teamRosterOpen, setTeamRosterOpen] = useState(false);
  const [selectedScoring, setSelectedScoring] = useState<PlayerGameLogScoring>(() => {
    if (typeof window === "undefined") return "ppr";
    const saved = window.localStorage.getItem("fantasy-board:profile-scoring");
    return saved === "standard" || saved === "halfPpr" || saved === "ppr" ? saved : "ppr";
  });
  const [selectedSeason, setSelectedSeason] = useState<number | null>(null);
  const [statsView, setStatsView] = useState<"logs" | "career">("logs");
  const [statsGridZoomIndex, setStatsGridZoomIndex] = useState(PLAYER_STATS_GRID_DEFAULT_ZOOM_INDEX);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const onResize = () => {
      setViewportWidth(window.innerWidth);
      setViewportHeight(window.innerHeight);
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  useEffect(() => {
    if (!isEmbedded || typeof window === "undefined") return undefined;
    const element = rootRef.current;
    if (!element) return undefined;

    const updateWidth = () => {
      setEmbeddedWidth(element.getBoundingClientRect().width);
    };

    updateWidth();

    if (typeof ResizeObserver === "undefined") {
      window.addEventListener("resize", updateWidth);
      return () => window.removeEventListener("resize", updateWidth);
    }

    const observer = new ResizeObserver(() => updateWidth());
    observer.observe(element);
    return () => observer.disconnect();
  }, [isEmbedded, player?.id]);

  useEffect(() => {
    if (!isOpen) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [isOpen, onClose]);

  useEffect(() => {
    if (!isOpen) setTeamRosterOpen(false);
  }, [isOpen]);

  useEffect(() => {
    return () => {
      if (closePointerTimeoutRef.current != null) {
        window.clearTimeout(closePointerTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    setStatsView("logs");
  }, [player?.id]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    window.localStorage.setItem("fantasy-board:profile-scoring", selectedScoring);
  }, [selectedScoring]);

  useEffect(() => {
    if (!gameLogs.length) {
      setSelectedSeason(null);
      return;
    }
    setSelectedSeason((current) => {
      if (current != null && gameLogs.some((season) => season.season === current)) return current;
      return [...gameLogs].sort((a, b) => b.season - a.season)[0]?.season ?? null;
    });
  }, [gameLogs]);

  const grouped = useMemo(() => {
    const map = new Map<string, PlayerProfileEntry[]>();
    for (const link of links ?? []) {
      const key = normalizePodcastKey(link.podcast);
      const arr = map.get(key) ?? [];
      arr.push(link);
      map.set(key, arr);
    }
    // stable-ish sorting: podcast alpha, then label alpha
    const entries = Array.from(map.entries()).sort((a, b) => a[0].localeCompare(b[0]));
    for (const [, arr] of entries) {
      arr.sort((a, b) => String(a.label).localeCompare(String(b.label)));
    }
    return entries;
  }, [links]);

  const teamRosterGroups = useMemo(() => {
    const team = formatTeamAbbreviation(player?.team);
    if (!team) return [];

    const grouped = new Map<string, Array<{ id: string; player: Player; rank: number }>>();
    rankingIds.forEach((id, index) => {
      const candidate = playersById[id];
      if (!candidate) return;
      if (formatTeamAbbreviation(candidate.team) !== team) return;
      const key = candidate.position || "Other";
      const bucket = grouped.get(key) ?? [];
      bucket.push({ id, player: candidate, rank: index + 1 });
      grouped.set(key, bucket);
    });

    const order = ["QB", "RB", "WR", "TE", "K", "DST"];
    return Array.from(grouped.entries())
      .sort((a, b) => {
        const aIndex = order.indexOf(a[0]);
        const bIndex = order.indexOf(b[0]);
        const safeA = aIndex === -1 ? order.length : aIndex;
        const safeB = bIndex === -1 ? order.length : bIndex;
        return safeA - safeB || a[0].localeCompare(b[0]);
      })
      .map(([position, items]) => ({ position, items }));
  }, [player?.team, playersById, rankingIds]);

  const activeSeasonLogs = useMemo(() => {
    if (!gameLogs.length) return null;
    return (
      gameLogs.find((season) => season.season === selectedSeason) ??
      [...gameLogs].sort((a, b) => b.season - a.season)[0] ??
      null
    );
  }, [gameLogs, selectedSeason]);
  const ppgValue = useMemo(() => {
    if (!activeSeasonLogs) return null;
    const points = activeSeasonLogs.rows
      .map((row) => getFantasyPointsForScoring(row, selectedScoring))
      .filter((value): value is number => value != null && Number.isFinite(value) && value > 0);
    if (!points.length) return null;
    return points.reduce((sum, value) => sum + value, 0) / points.length;
  }, [activeSeasonLogs, selectedScoring]);
  const positionSeasonRank = useMemo(() => {
    if (!player?.id || !player.position || !activeSeasonLogs) return null;
    const season = activeSeasonLogs.season;
    const rows = Object.entries(playerGameLogsById)
      .map(([playerId, seasons]) => {
        const candidate = playersById[playerId];
        if (!candidate || candidate.position !== player.position) return null;
        const candidateSeason = seasons.find((entry) => entry.season === season);
        const total = getSeasonFantasyTotal(candidateSeason, selectedScoring);
        if (total == null) return null;
        return { playerId, total };
      })
      .filter((entry): entry is { playerId: string; total: number } => Boolean(entry))
      .sort((a, b) => b.total - a.total || a.playerId.localeCompare(b.playerId));

    const index = rows.findIndex((entry) => entry.playerId === player.id);
    return index >= 0 ? index + 1 : null;
  }, [activeSeasonLogs, player?.id, player?.position, playersById, selectedScoring]);
  const positionSeasonRankLabel = `Pos Rank ('${formatSeasonShortLabel(activeSeasonLogs?.season)})`;
  const stats = [
    { label: "Age", value: player?.age != null && player.age >= 0 ? String(player.age) : "—" },
    { label: "Exp", value: player?.yearsExp != null ? (player.yearsExp === 0 ? "Rookie" : String(player.yearsExp)) : "—" },
    { label: "Height", value: player?.height ?? "—" },
    { label: "Weight", value: player?.weight ?? "—" },
  ];

  if (!isOpen) return null;

  const accent = player?.position ? posColor(player.position) : "#0f172a";
  const teamAccent = player?.team ? getTeamPrimaryColor(player.team, accent) : accent;
  const teamWatermarkStyle = getTeamWatermarkStyle(player?.team);
  const profileBackground = getTeamProfileBackground(player?.team, teamAccent);
  const responsiveWidth = isEmbedded && embeddedWidth > 0 ? embeddedWidth : viewportWidth;
  const mobileMode = isEmbedded ? responsiveWidth <= 760 : viewportWidth <= 1100;
  const mobileLandscape = mobileMode && !isEmbedded && viewportWidth > viewportHeight;
  const mobilePopupGap = 5;
  const mobileModalTopGap = mobileMode && !isEmbedded && !mobileLandscape && isNativeIosShell()
    ? "max(env(safe-area-inset-top), 54px)"
    : `${mobilePopupGap}px`;
  const mobileModalHeight = `calc(100dvh - (${mobileModalTopGap}) - ${mobilePopupGap}px)`;
  const hasRealProfileImage = Boolean(
    player?.imageUrl && !player.imageUrl.includes("/headshot-placeholder.svg")
  );
  const teamDisplayName = player?.team ? formatTeamFullName(player.team, "Free Agent") : "Free Agent";
  const teamStripFontSize = mobileMode
    ? teamDisplayName.length >= 20
      ? 10
      : teamDisplayName.length >= 16
        ? 11
        : teamDisplayName.length >= 12
          ? 13
          : 16
    : teamDisplayName.length >= 20
      ? 18
      : teamDisplayName.length >= 14
        ? 21
        : 24;
  const gameLogPosition: PlayerGameLogPosition | null =
    player?.position === "QB" || player?.position === "RB" || player?.position === "WR" || player?.position === "TE"
      ? player.position
      : null;
  const drafted = player ? draftedIds?.has(player.id) ?? false : false;

  return (
    <div
      ref={rootRef}
      role={isEmbedded ? "region" : "dialog"}
      aria-modal={isEmbedded ? undefined : "true"}
      style={{
        position: isEmbedded ? "relative" : "fixed",
        inset: isEmbedded ? undefined : 0,
        zIndex: isEmbedded ? "auto" : 9999,
        background: isEmbedded ? "transparent" : "rgba(0,0,0,0.55)",
        display: "flex",
        alignItems: isEmbedded ? "stretch" : "center",
        justifyContent: isEmbedded ? "stretch" : "center",
        padding: isEmbedded ? 0 : mobileMode ? `${mobileModalTopGap} ${mobilePopupGap}px ${mobilePopupGap}px` : 16,
        width: "100%",
        height: isEmbedded ? "100%" : mobileMode ? "100dvh" : "100%",
        boxSizing: "border-box",
      }}
      onMouseDown={(e) => {
        // click outside closes
        if (!isEmbedded && e.target === e.currentTarget) onClose();
      }}
    >
      <div
        style={{
          position: "relative",
          width: isEmbedded ? "100%" : mobileMode ? `calc(100vw - ${mobilePopupGap * 2}px)` : "min(1160px, 100%)",
          maxWidth: isEmbedded ? "100%" : mobileMode ? `calc(100vw - ${mobilePopupGap * 2}px)` : "min(1160px, 100%)",
          height: isEmbedded ? "100%" : mobileMode ? mobileModalHeight : undefined,
          maxHeight: isEmbedded ? "100%" : mobileMode ? mobileModalHeight : "calc(100vh - 32px)",
          background: "rgba(18,18,22,0.98)",
          border: "1px solid rgba(255,255,255,0.10)",
          borderRadius: isEmbedded ? 0 : mobileMode ? 18 : 18,
          boxShadow: isEmbedded ? "none" : "0 20px 60px rgba(0,0,0,0.45)",
          overflowX: "hidden",
          overflowY: mobileLandscape ? "auto" : "hidden",
          display: "flex",
          flexDirection: "column",
          overscrollBehavior: mobileLandscape ? "contain" : undefined,
          WebkitOverflowScrolling: mobileLandscape ? "touch" : undefined,
        }}
      >
        <div
          style={{
            position: "relative",
            minHeight: mobileMode ? 156 : 200,
            flex: "0 0 auto",
            display: "grid",
            gridTemplateColumns: mobileMode ? "minmax(112px, 0.9fr) minmax(0, 1.1fr)" : "minmax(180px, 0.82fr) minmax(0, 1.18fr)",
            background: profileBackground,
          }}
        >
          {mobileMode && player && onToggleDrafted ? (
            <button
              type="button"
              onPointerDown={(event) => {
                event.preventDefault();
                event.stopPropagation();
                lastDraftTogglePointerAtRef.current = Date.now();
                onToggleDrafted(player.id);
              }}
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                if (Date.now() - lastDraftTogglePointerAtRef.current < 500) return;
                onToggleDrafted(player.id);
              }}
              aria-label={drafted ? `Undraft ${player.name}` : `Draft ${player.name}`}
              style={{
                position: "absolute",
                top: 10,
                left: 10,
                zIndex: 2,
                appearance: "none",
                width: 42,
                height: 42,
                borderRadius: 999,
                border: drafted ? "1px solid rgba(248,113,113,0.78)" : "1px solid rgba(34,197,94,0.48)",
                background: drafted ? "rgba(17,24,39,0.72)" : "rgba(6,78,59,0.72)",
                color: drafted ? "rgb(248,113,113)" : "rgb(74,222,128)",
                fontSize: 24,
                fontWeight: 900,
                lineHeight: 1,
                cursor: "pointer",
                boxShadow: drafted
                  ? "0 8px 22px rgba(0,0,0,0.34), inset 0 0 0 1px rgba(255,255,255,0.08)"
                  : "0 8px 22px rgba(0,0,0,0.26), inset 0 0 0 1px rgba(255,255,255,0.08)",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                touchAction: "manipulation",
              }}
              >
                {drafted ? "−" : "+"}
              </button>
            ) : null}

          <div
            aria-hidden
            style={{
              position: "absolute",
              inset: 0,
              background:
                formatTeamAbbreviation(player?.team) === "NYJ" || formatTeamAbbreviation(player?.team) === "NYG"
                  ? "linear-gradient(90deg, rgba(0,0,0,0.04) 0%, rgba(0,0,0,0.14) 40%, rgba(0,0,0,0.2) 100%)"
                  : "linear-gradient(90deg, rgba(0,0,0,0.08) 0%, rgba(0,0,0,0.22) 40%, rgba(0,0,0,0.28) 100%)",
            }}
          />

          {player?.team ? (
            <div
              aria-hidden
              style={{
                position: "absolute",
                inset: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                transform: mobileMode ? "translate(12px, -3px) scale(1.4)" : "translate(30px, -5px) scale(1.85)",
                pointerEvents: "none",
                ...teamWatermarkStyle,
              }}
            >
              <TeamLogo
                team={player.team}
                size={120}
                style={{
                  width: mobileMode ? 58 : 76,
                  height: mobileMode ? 58 : 76,
                }}
                fallback={<span style={{ fontSize: 90, fontWeight: 900 }}>{formatTeamAbbreviation(player.team)}</span>}
              />
            </div>
          ) : null}

          <div
            style={{
              position: "relative",
              minHeight: mobileMode ? 156 : 200,
              display: "flex",
              alignItems: "flex-end",
              justifyContent: "center",
              overflow: "hidden",
            }}
          >
            {hasRealProfileImage ? (
              <img
                src={player?.imageUrl}
                alt={player?.name ?? "Player"}
                style={{
                  maxHeight: mobileMode ? 154 : 190,
                  width: "auto",
                  objectFit: "contain",
                  alignSelf: "flex-end",
                  filter: "drop-shadow(0 10px 30px rgba(0,0,0,0.28))",
                }}
                onError={(event) => {
                  event.currentTarget.style.display = "none";
                }}
              />
            ) : null}
            <div
              style={{
                position: "absolute",
                left: 0,
                right: 0,
                bottom: 0,
                display: "flex",
                alignItems: "stretch",
                fontWeight: 900,
                letterSpacing: 0.4,
                textTransform: "uppercase",
              }}
            >
              <div
                style={{
                  background: accent,
                  color: "#fff",
                  padding: mobileMode ? "8px 10px" : "11px 14px",
                  fontSize: mobileMode ? 14 : 18,
                  lineHeight: 1,
                  minWidth: mobileMode ? 46 : 60,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                {player?.position ?? "—"}
              </div>
              <div
                style={{
                  background: mobileMode ? "rgba(0,0,0,0.58)" : "rgba(0,0,0,0.38)",
                  color: "#fff",
                  padding: mobileMode ? "8px 12px 8px 10px" : "11px 18px 11px 12px",
                  fontSize: teamStripFontSize,
                  lineHeight: 1,
                  clipPath: mobileMode ? "none" : "polygon(0 0, calc(100% - 24px) 0, 100% 100%, 0 100%)",
                  display: "flex",
                  alignItems: "center",
                  minWidth: 0,
                  flex: 1,
                }}
              >
                {player?.team ? (
                  <button
                    type="button"
                    onClick={() => setTeamRosterOpen(true)}
                    title={`Show ${teamDisplayName} players`}
                    style={{
                      appearance: "none",
                      border: "none",
                      background: "transparent",
                      padding: 0,
                      margin: 0,
                      color: "inherit",
                      textDecoration: "none",
                      display: "inline-block",
                      maxWidth: "100%",
                      cursor: "pointer",
                      font: "inherit",
                      textAlign: "left",
                    }}
                  >
                    <span
                      style={{
                        position: "relative",
                        display: "inline-block",
                        maxWidth: "100%",
                        paddingRight: mobileMode ? 14 : 16,
                      }}
                    >
                      <span
                        style={{
                          whiteSpace: mobileMode ? "normal" : "nowrap",
                          overflow: "hidden",
                          textOverflow: mobileMode ? undefined : "ellipsis",
                          letterSpacing: mobileMode ? 0 : 0.25,
                          lineHeight: mobileMode ? 1.08 : 1,
                          display: mobileMode ? "-webkit-box" : "block",
                          WebkitLineClamp: mobileMode ? 2 : undefined,
                          WebkitBoxOrient: mobileMode ? "vertical" : undefined,
                          paddingBottom: mobileMode ? 2 : 0,
                          marginBottom: mobileMode ? -2 : 0,
                          maxWidth: "100%",
                        }}
                      >
                        {teamDisplayName}
                      </span>
                      <span
                        aria-hidden="true"
                        style={{
                          position: "absolute",
                          top: mobileMode ? -1 : -2,
                          right: 0,
                          color: "rgba(255,255,255,0.9)",
                          fontSize: mobileMode ? 10 : 12,
                          lineHeight: 1,
                        }}
                      >
                        ↗
                      </span>
                    </span>
                  </button>
                ) : (
                  <span
                    style={{
                      width: "100%",
                      whiteSpace: mobileMode ? "normal" : "nowrap",
                      overflow: "hidden",
                      textOverflow: mobileMode ? undefined : "ellipsis",
                      letterSpacing: mobileMode ? 0 : 0.25,
                      lineHeight: mobileMode ? 1.08 : 1,
                      display: mobileMode ? "-webkit-box" : "block",
                      WebkitLineClamp: mobileMode ? 2 : undefined,
                      WebkitBoxOrient: mobileMode ? "vertical" : undefined,
                      paddingBottom: mobileMode ? 2 : 0,
                      marginBottom: mobileMode ? -2 : 0,
                    }}
                  >
                    {teamDisplayName}
                  </span>
                )}
              </div>
            </div>
          </div>

          <div
            style={{
              position: "relative",
              zIndex: 1,
              padding: mobileMode ? "6px 10px 8px 6px" : "12px 18px 12px 6px",
              display: "flex",
              flexDirection: "column",
              justifyContent: "space-between",
              gap: mobileMode ? 4 : 9,
            }}
          >
            <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: mobileMode ? 6 : 8 }}>
              <div style={{ display: "flex", alignItems: "center", gap: mobileMode ? 6 : 8 }}>
                <button
                  type="button"
                  onPointerDown={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    lastClosePointerAtRef.current = Date.now();
                    if (closePointerTimeoutRef.current != null) {
                      window.clearTimeout(closePointerTimeoutRef.current);
                      closePointerTimeoutRef.current = null;
                    }
                  }}
                  onClick={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    onClose();
                  }}
                  aria-label="Close player profile"
                  style={{
                    appearance: "none",
                    width: mobileMode ? 30 : 36,
                    height: mobileMode ? 30 : 36,
                    borderRadius: 999,
                    border: "1px solid rgba(255,255,255,0.22)",
                    background: "rgba(255,255,255,0.14)",
                    color: "white",
                    fontSize: mobileMode ? 16 : 20,
                    lineHeight: 1,
                    cursor: "pointer",
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    touchAction: "manipulation",
                  }}
                >
                  ×
                </button>
              </div>
            </div>

            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: mobileMode ? "clamp(18px, 6.2vw, 28px)" : "clamp(28px, 3vw, 40px)", fontWeight: 900, lineHeight: 0.92, color: "#fff" }}>
                {player?.name ?? "Player"}
              </div>
              <div
                style={{
                  marginTop: mobileMode ? 5 : 7,
                  display: "grid",
                  gridTemplateColumns: mobileMode ? "repeat(2, minmax(0, 1fr))" : "repeat(2, max-content)",
                  gap: mobileMode ? 8 : 12,
                  alignItems: "start",
                  width: mobileMode ? "100%" : "fit-content",
                }}
              >
                <div
                  style={{
                    display: "grid",
                    gap: 2,
                    minWidth: 0,
                    justifyItems: "start",
                    alignContent: "start",
                  }}
                >
                  <div
                    style={{
                      fontSize: mobileMode ? 10 : 12,
                      fontWeight: 900,
                      letterSpacing: 1.1,
                      textTransform: "uppercase",
                      color: "rgba(255,255,255,0.72)",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {activeSeasonLogs?.season ?? "—"} PPG
                  </div>
                  <div
                    style={{
                      fontSize: mobileMode ? "clamp(16px, 4.8vw, 22px)" : "clamp(20px, 2.2vw, 28px)",
                      fontWeight: 900,
                      lineHeight: 1,
                      color: "#fff",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {formatPpg(ppgValue)}
                  </div>
                </div>
                <div
                  style={{
                    display: "grid",
                    gap: 2,
                    minWidth: 0,
                    justifyItems: "start",
                    alignContent: "start",
                  }}
                >
                  <div
                    style={{
                      fontSize: mobileMode ? 10 : 12,
                      fontWeight: 900,
                      letterSpacing: 1.1,
                      textTransform: "uppercase",
                      color: "rgba(255,255,255,0.72)",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {positionSeasonRankLabel}
                  </div>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "flex-start",
                      gap: mobileMode ? 6 : 8,
                      width: "fit-content",
                      minHeight: mobileMode ? 22 : 28,
                    }}
                  >
                    <span
                      style={{
                        fontSize: mobileMode ? "clamp(16px, 4.8vw, 22px)" : "clamp(20px, 2.2vw, 28px)",
                        fontWeight: 900,
                        lineHeight: 1,
                        color: "#fff",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {positionSeasonRank ?? "—"}
                    </span>
                  </div>
                </div>
              </div>
              <div
                style={{
                  marginTop: mobileMode ? 6 : 10,
                  display: "grid",
                  gridTemplateColumns: mobileMode ? "repeat(2, minmax(0, 1fr))" : "repeat(4, max-content)",
                  gap: mobileMode ? 6 : 8,
                  width: mobileMode ? "100%" : "fit-content",
                  justifyContent: mobileMode ? "stretch" : "start",
                }}
              >
                {stats.map((stat) => (
                  <div
                    key={stat.label}
                    style={{
                      minWidth: mobileMode ? 0 : "max-content",
                      paddingLeft: mobileMode ? 0 : stat.label === "Age" ? 0 : 8,
                      paddingRight: mobileMode ? 0 : 8,
                      borderLeft: mobileMode ? "none" : stat.label === "Age" ? "none" : "1px solid rgba(255,255,255,0.34)",
                    }}
                  >
                    <div
                      style={{
                        fontSize: mobileMode ? 10 : 12,
                        fontWeight: 900,
                        letterSpacing: 1.1,
                        textTransform: "uppercase",
                        color: "rgba(255,255,255,0.72)",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {stat.label}
                    </div>
                    <div
                      style={{
                        marginTop: 2,
                        fontSize: mobileMode ? "clamp(16px, 4.8vw, 22px)" : "clamp(20px, 2.2vw, 28px)",
                        fontWeight: 900,
                        lineHeight: 1,
                        color: "#fff",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {stat.label === "Weight" && stat.value !== "—" ? (
                        <>
                          {stat.value}
                          <span
                            style={{
                              fontSize: "0.56em",
                              marginLeft: 6,
                              opacity: 0.9,
                            }}
                          >
                            lbs
                          </span>
                        </>
                      ) : (
                        stat.value
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {teamRosterOpen ? (
          <div
            style={{
              position: "absolute",
              inset: 0,
              zIndex: 8,
              background: "rgba(4,7,18,0.58)",
              backdropFilter: "blur(3px)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: mobileMode ? 14 : 20,
            }}
            onMouseDown={(event) => {
              if (event.target === event.currentTarget) setTeamRosterOpen(false);
            }}
          >
            <div
              style={{
                width: mobileMode ? "100%" : "min(520px, 100%)",
                maxHeight: mobileMode ? "min(72vh, 100%)" : "min(68vh, 100%)",
                borderRadius: 18,
                border: "1px solid rgba(255,255,255,0.12)",
                background: "rgba(18,18,22,0.98)",
                boxShadow: "0 20px 50px rgba(0,0,0,0.42)",
                overflow: "hidden",
                display: "flex",
                flexDirection: "column",
              }}
              onMouseDown={(event) => event.stopPropagation()}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 12,
                  padding: mobileMode ? "14px 14px 10px" : "16px 18px 12px",
                  borderBottom: "1px solid rgba(255,255,255,0.08)",
                }}
              >
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: mobileMode ? 18 : 22, fontWeight: 900, lineHeight: 1.05, color: "#fff" }}>
                    {teamDisplayName}
                  </div>
                  <div style={{ marginTop: 4, fontSize: 11, fontWeight: 800, letterSpacing: 0.4, textTransform: "uppercase", color: "rgba(255,255,255,0.62)" }}>
                    Current Rankings List
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setTeamRosterOpen(false)}
                  style={{
                    width: 32,
                    height: 32,
                    borderRadius: 999,
                    border: "1px solid rgba(255,255,255,0.12)",
                    background: "rgba(255,255,255,0.06)",
                    color: "#fff",
                    fontSize: 22,
                    lineHeight: 1,
                    cursor: "pointer",
                    flex: "0 0 auto",
                  }}
                >
                  ×
                </button>
              </div>

              <div
                style={{
                  padding: mobileMode ? 12 : 16,
                  overflowY: "auto",
                  display: "grid",
                  gap: 12,
                  scrollbarWidth: "none",
                  msOverflowStyle: "none",
                }}
                className="playerProfileScroll"
              >
                {teamRosterGroups.length > 0 ? (
                  teamRosterGroups.map((group) => (
                    <div key={group.position} style={{ display: "grid", gap: 8 }}>
                      <div style={{ fontSize: 11, fontWeight: 900, letterSpacing: 0.5, textTransform: "uppercase", color: "rgba(255,255,255,0.64)" }}>
                        {group.position}
                      </div>
                      <div style={{ display: "grid", gap: 6 }}>
                        {group.items.map(({ id, player: teammate, rank }) => (
                          <div
                            key={id}
                            style={{
                              display: "grid",
                              gridTemplateColumns: mobileMode ? "34px 34px minmax(0, 1fr)" : "40px 40px minmax(0, 1fr)",
                              alignItems: "center",
                              gap: mobileMode ? 8 : 10,
                              padding: mobileMode ? "8px 10px" : "9px 12px",
                              borderRadius: 12,
                              background: "rgba(255,255,255,0.05)",
                              border: "1px solid rgba(255,255,255,0.06)",
                            }}
                          >
                            <div style={{ fontSize: mobileMode ? 12 : 13, fontWeight: 900, color: "rgba(255,255,255,0.68)", textAlign: "center" }}>
                              {rank}
                            </div>
                            <img
                              src={teammate.imageUrl || "/headshot-placeholder.svg"}
                              alt={teammate.name}
                              style={{
                                width: mobileMode ? 34 : 40,
                                height: mobileMode ? 34 : 40,
                                borderRadius: 999,
                                objectFit: "cover",
                                background: "rgba(255,255,255,0.18)",
                                border: "1px solid rgba(255,255,255,0.12)",
                              }}
                              onError={(event) => {
                                const img = event.currentTarget;
                                if (!img.src.includes("/headshot-placeholder.svg")) img.src = "/headshot-placeholder.svg";
                              }}
                            />
                            <div style={{ minWidth: 0, display: "grid", gap: 2 }}>
                              <div style={{ fontSize: mobileMode ? 14 : 15, fontWeight: 800, color: "#fff", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                                {teammate.name}
                              </div>
                              <div style={{ fontSize: mobileMode ? 11 : 12, fontWeight: 700, color: "rgba(255,255,255,0.68)" }}>
                                {teammate.position} · {formatTeamFullName(teammate.team, "Free Agent")}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))
                ) : (
                  <div style={{ padding: mobileMode ? "6px 2px" : "8px 4px", fontSize: 14, color: "rgba(255,255,255,0.78)" }}>
                    No players from {teamDisplayName} are in the current rankings list.
                  </div>
                )}
              </div>
            </div>
          </div>
        ) : null}

        <div
          style={{
            padding: mobileMode ? 0 : 16,
            overflowY: mobileLandscape ? "visible" : "auto",
            overscrollBehavior: "contain",
            minHeight: 0,
            flex: mobileLandscape ? "0 0 auto" : "1 1 auto",
            display: "flex",
            flexDirection: "column",
            scrollbarWidth: "none",
            msOverflowStyle: "none",
          }}
          className="playerProfileScroll"
        >
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: mobileMode ? 0 : 18,
              minHeight: mobileLandscape ? undefined : "100%",
              height: mobileLandscape ? undefined : "100%",
              flex: mobileLandscape ? "0 0 auto" : "1 1 auto",
            }}
          >
            {gameLogs.length > 0 ? (
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: mobileMode ? 10 : 16,
                  flex: mobileLandscape ? "0 0 auto" : mobileMode ? "1 1 auto" : undefined,
                  minHeight: 0,
                }}
              >
                {statsView === "career" ? (
                  <PlayerCareerTable
                    seasons={gameLogs}
                    position={gameLogPosition}
                    selectedScoring={selectedScoring}
                    onSelectedScoringChange={setSelectedScoring}
                    statsView={statsView}
                    onStatsViewChange={setStatsView}
                    gridZoomIndex={statsGridZoomIndex}
                    onGridZoomIndexChange={setStatsGridZoomIndex}
                  />
                ) : (
                  <PlayerGameLogTable
                    seasons={gameLogs}
                    position={gameLogPosition}
                    selectedScoring={selectedScoring}
                    onSelectedScoringChange={setSelectedScoring}
                    selectedSeason={selectedSeason}
                    onSelectedSeasonChange={setSelectedSeason}
                    statsView={statsView}
                    onStatsViewChange={setStatsView}
                    gridZoomIndex={statsGridZoomIndex}
                    onGridZoomIndexChange={setStatsGridZoomIndex}
                  />
                )}
              </div>
            ) : (
              <section
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: mobileMode ? 10 : 16,
                  minHeight: "100%",
                  height: "100%",
                  flex: "1 1 auto",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: mobileMode ? 10 : 12,
                    flexWrap: "wrap",
                    padding: mobileMode ? "14px 14px 0" : 0,
                  }}
                >
                  <div style={{ display: "grid", gap: 2 }}>
                    <div style={{ fontSize: mobileMode ? "clamp(20px, 6vw, 26px)" : "clamp(22px, 2vw, 30px)", fontWeight: 900, letterSpacing: 0.2 }}>
                      Game Logs
                    </div>
                    <div style={{ fontSize: 12, fontWeight: 800, letterSpacing: 1, textTransform: "uppercase", color: "rgba(255,255,255,0.56)" }}>
                      Historical Data
                    </div>
                  </div>
                </div>

                <div
                  style={{
                    margin: 0,
                    minHeight: mobileMode ? "100%" : 0,
                    flex: "1 1 auto",
                    borderRadius: mobileMode ? 0 : 16,
                    border: mobileMode ? "none" : "1px solid rgba(255,255,255,0.08)",
                    background: "rgba(18,18,22,0.98)",
                    display: "grid",
                    placeItems: "center",
                    padding: mobileMode ? 18 : 28,
                    textAlign: "center",
                  }}
                >
                  <div style={{ display: "grid", gap: 8 }}>
                    <div style={{ fontSize: mobileMode ? 18 : 22, fontWeight: 900, color: "#fff" }}>
                      No Historical Data Available
                    </div>
                    <div style={{ fontSize: mobileMode ? 13 : 14, fontWeight: 700, color: "rgba(255,255,255,0.62)" }}>
                      Game log stats have not been imported for this player yet.
                    </div>
                  </div>
                </div>
              </section>
            )}

            {(links?.length ?? 0) > 0 ? (
              <div style={{ display: "grid", gap: 14, padding: mobileMode ? 14 : 0 }}>
                <div style={{ fontSize: 22, fontWeight: 900, letterSpacing: 0.3 }}>Profile Links</div>
                <div style={{ display: "grid", gap: 14 }}>
              {grouped.map(([podcast, items]) => (
                <div
                  key={podcast}
                  style={{
                    border: "1px solid rgba(255,255,255,0.10)",
                    borderRadius: 16,
                    padding: 14,
                    background: "rgba(38,38,38,0.9)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 12,
                  }}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: 12, minWidth: 0 }}>
                    <PodcastLogo podcast={podcast} />
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontSize: 18, fontWeight: 700, lineHeight: 1.2, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                        {podcast}
                      </div>
                      <div style={{ opacity: 0.65, fontSize: 12, marginTop: 2 }}>
                        {items.length} link{items.length === 1 ? "" : "s"}
                      </div>
                    </div>
                  </div>

                  <div style={{ display: "flex", flexWrap: "wrap", gap: 8, justifyContent: "flex-end" }}>
                    {items.map((it, i) => (
                      <a
                        key={`${it.podcast}-${it.label}-${i}`}
                        href={it.url}
                        target="_blank"
                        rel="noreferrer"
                        style={{
                          textDecoration: "none",
                          color: "white",
                          border: "1px solid rgba(255,255,255,0.16)",
                          background: "rgba(0,0,0,0.32)",
                          padding: "8px 10px",
                          borderRadius: 999,
                          fontWeight: 700,
                          fontSize: 13,
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 8,
                        }}
                        title={it.url}
                      >
                        {it.label}
                        <span aria-hidden="true" style={{ opacity: 0.75 }}>↗</span>
                      </a>
                    ))}
                  </div>
                </div>
              ))}
                </div>
              </div>
            ) : null}

          </div>
        </div>
      </div>
    </div>
  );
}
